import React, { FC, useState, ChangeEvent, useCallback, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Modal,
  ModalType,
  Table,
  ButtonColor,
  ButtonSize,
  Checkbox,
} from '@code/innovance-react-ui-kit';
import {
  Container,
  Title,
  Subtitle,
  Description,
  TableWrapper,
  WarningText,
  FormRow,
} from 'src/front/components/FormStyles';
import Markdown from 'src/front/components//Markdown';
import CustomerSearchForm, {
  SearchFormData,
  customerSearchDefaultValues,
} from 'src/front/components//CustomerSearchForm';
import AsyncButton from 'src/front/components//AsyncButton';
import {
  useCustomerSearchDispatch,
  useCustomerSearchState,
  useClearDispatch,
} from 'src/front/contexts/LoanApplicationContext';
import { CustomerTableRow } from 'src/front/pages/applications/card-entry/CustomerSearch';
import { Customer, LoanIndividualPageValidation } from 'src/front/api/apiInstance';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';

const tableHeaders = [
  'loanApp_customerTable_name_title',
  'loanApp_customerTable_Patronymic_title',
  'loanApp_customerTable_birth_title',
  'loanApp_customerTable_gender_title',
  'loanApp_customerTable_document_title',
];

interface Props {
  goToNextStep: () => void;
}

const CustomerSearch: FC<Props> = ({ goToNextStep }) => {
  const { t } = useTranslation();
  const dispatch = useCustomerSearchDispatch();
  const {
    customerSearchValues,
    pageNumber: initialCustomerPage,
    customerId: selectedCustomerId,
    isNewCustomer: initialIsNewCustomer,
  } = useCustomerSearchState();
  const clearCustomerSearchState = useClearDispatch(true);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [customerList, setCustomerList] = useState<CustomerTableRow[]>();
  const [page, setPage] = useState<number>(initialCustomerPage || 1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const [warning, setWarning] = useState<string>();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTableRow>();
  const [selectedCustomerPage, setSelectedCustomerPage] = useState<number>();
  const [isNewCustomer, setIsNewCustomer] = useState<boolean>(initialIsNewCustomer || false);

  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(
    customerSearchValues || customerSearchDefaultValues
  );
  const loadCustomerList = useCallback(
    async (
      pageNumber?: number,
      forceRefresh = false,
      formValues: SearchFormData = searchFormValues
    ): Promise<CustomerTableRow[]> => {
      try {
        setLoadingTable(true);

        const { birthDate, ...rest } = formValues;
        const { data } = await Customer.getCustomers({
          ...rest,
          birthDate: formatDate(birthDate),
          rowCountPerPage: 5,
          pageNumber: pageNumber || page,
          refresh: !pageNumber || forceRefresh,
        });
        const customers = data.customerList.map((customer) => ({
          id: customer.customerId || '',
          columns: [
            `${customer.customerName} ${customer.customerSurname}`,
            customer.customerSecondName || '',
            `${customer.birthDate || ''} - ${customer.birthPlace || ''}`,
            customer.gender || '',
            `${customer.identityDocumentSerialCode} - ${customer.identityDocumentSerialNumber}`,
          ],
          customerNumber: customer.customerNumber,
        }));
        setWarning(data.warning);
        setHasNextPage(data.hasNextPage);
        setPage(pageNumber || 1);

        setCustomerList(customers);
        setLoadingTable(false);

        return customers;
      } catch (error) {
        return [];
      }
    },
    [page, searchFormValues]
  );
  const toggleModal = (): void => setIsModalOpen((state) => !state);
  const loadPageItems = useCallback(
    async (pageNumber?: number): Promise<void> => {
      loadCustomerList(pageNumber);
    },
    [loadCustomerList]
  );

  useEffect(() => {
    const loadPreviousValues = async (): Promise<void> => {
      const customers = await loadCustomerList(initialCustomerPage, true);
      setSelectedCustomer(customers?.find((customer) => customer.id === selectedCustomerId));
    };

    if (!customerList && !selectedCustomer && (selectedCustomerId || !!initialIsNewCustomer)) {
      loadPreviousValues();
    }
  }, [
    customerList,
    customerSearchValues,
    initialCustomerPage,
    initialIsNewCustomer,
    loadCustomerList,
    selectedCustomer,
    selectedCustomerId,
  ]);
  const onCustomerSubmit = async (): Promise<void> => {
    dispatch({
      isNewCustomer: !selectedCustomer || isNewCustomer,
      customerId: selectedCustomer?.id,
      customerNumber: selectedCustomer?.customerNumber,
      customerSearchValues: searchFormValues,
      pageNumber: selectedCustomerPage,
    });
    const {
      data,
    } = await LoanIndividualPageValidation.validateLoanIndividualPageDataForCustomerChange({
      customerId: isNewCustomer ? '' : selectedCustomer?.id,
    });

    if (data.result === 'OK') {
      goToNextStep();
    }
  };
  const handleContinue = async (): Promise<void> => {
    if (!isNewCustomer && !selectedCustomer) {
      setWarning(t('cardEntry_customersearch_noSelectedCustomer_errorMessage'));
    } else if (customerList && customerList.length > 0 && !selectedCustomer) {
      toggleModal();
    } else {
      await onCustomerSubmit();
    }
  };
  const handleNewCustomer = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setIsNewCustomer(target.checked);
    clearCustomerSearchState();
    selectedCustomer && setSelectedCustomer(undefined);
    selectedCustomerPage && setSelectedCustomerPage(undefined);
    warning && setWarning(undefined);
  };

  const handleSelectCustomer = (customer: CustomerTableRow | undefined): void => {
    setSelectedCustomer(customer);
    setSelectedCustomerPage(page);

    isNewCustomer && setIsNewCustomer(false);
    warning && setWarning(undefined);
  };
  const onSearchSubmit = (formData: SearchFormData): void => {
    setSearchFormValues(formData);
    clearCustomerSearchState();
    selectedCustomer && setSelectedCustomer(undefined);
    selectedCustomerPage && setSelectedCustomerPage(undefined);
    isNewCustomer && setIsNewCustomer(false);
    loadCustomerList(1, true, formData);
  };
  const getConfirmMessage = (): string => {
    if (isNewCustomer) return t('cardEntry_customerInfo_newCustomerConfirm_text');

    return t('cardEntry_customerInfo_existingCustomerConfirm_text', {
      customerNumber: selectedCustomer?.customerNumber,
    });
  };

  const confirmMessage = getConfirmMessage();
  return (
    <Container>
      {isModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={<Markdown text={confirmMessage} />}
          onCancel={toggleModal}
          onSubmit={onCustomerSubmit}
          submitText={t('general_components_confirm_button')}
          title={t('general_confirmationPopUp_title')}
          type={ModalType.Confirmation}
        />
      ) : null}
      <Title>
        <Trans i18nKey="loanApp_customerInfo_individualInformation_subtitle" />
      </Title>
      <Subtitle>
        <Trans i18nKey="loanApp_customerInfo_CustomerSearch_text" />
      </Subtitle>
      <CustomerSearchForm formValues={searchFormValues} onSubmit={onSearchSubmit} />
      {customerList ? (
        <>
          <Subtitle>
            <Trans i18nKey="loanApp_customerInfo_customerList_text" />
          </Subtitle>
          <Description>
            <Trans i18nKey="loanApp_customerInfo_listExplanation_text" />
          </Description>
          <TableWrapper>
            <Table
              disabled={isNewCustomer}
              emptyTableText={t('loanApp_customerInfo_notFound_text')}
              hasNextPage={hasNextPage}
              headers={tableHeaders.map((head) => t(head))}
              items={customerList}
              loading={loadingTable}
              loadPageItems={loadPageItems}
              onSelect={handleSelectCustomer}
              pageNumber={page}
              selectedItem={selectedCustomer}
              widths={['20', '15', '30', '12', '23']}
              clearItemOnPageChange
              withRadio
            />
          </TableWrapper>
          {warning ? (
            <WarningText>
              <Markdown text={warning} />
            </WarningText>
          ) : null}

          <FormRow>
            <Checkbox
              id="newCustomer"
              label={t('loanApp_customerInfo_newCustomer_button')}
              onChange={handleNewCustomer}
              value={isNewCustomer}
            />
          </FormRow>
          <FormRow>
            <AsyncButton
              color={ButtonColor.Primary}
              disabled={loadingTable}
              onClick={handleContinue}
              size={ButtonSize.Medium}
              text={t('general_continue_button')}
            />
          </FormRow>
        </>
      ) : null}
    </Container>
  );
};

export default CustomerSearch;
